import { createApi } from '@reduxjs/toolkit/query/react';
import {
  CreateEmployeeBodyPrivate,
  EmployeeAuthPasswordResponsePrivate,
  EmployeeResponsePrivate,
  GetManyEmployeesQueryPrivate,
  GetManyEmployeesResponsePrivate,
  UpdateEmployeeAuthPasswordBodyPrivate,
  UpdateEmployeeBodyPrivate,
} from '@tests/types';

import { ApiPath } from '@/constants';
import { createUrl } from '@/utils';

import { authGuardQuery } from './queries';

export const employeesApi = createApi({
  baseQuery: authGuardQuery,
  endpoints: (build) => ({
    createEmployee: build.mutation<EmployeeResponsePrivate, CreateEmployeeBodyPrivate>({
      query: (data) => ({
        data,
        method: 'POST',
        url: createUrl(ApiPath.Employees),
      }),
    }),
    getEmployeeAuthPasswordById: build.query<
      EmployeeAuthPasswordResponsePrivate,
      { id: number | string }
    >({
      query: ({ id }) => ({
        method: 'GET',
        url: createUrl(ApiPath.Employees, id, ApiPath.AuthPassword),
      }),
    }),
    getEmployeeById: build.query<EmployeeResponsePrivate, { id: number | string }>({
      providesTags: (employee) => [{ id: employee?.id, type: 'Employees' }],
      query: ({ id }) => ({
        method: 'GET',
        url: createUrl(ApiPath.Employees, id),
      }),
    }),
    getEmployeesList: build.query<
      GetManyEmployeesResponsePrivate,
      RelationsParams & Partial<GetManyEmployeesQueryPrivate>
    >({
      providesTags: (result) => [
        ...(result?.results?.map((employee) => ({
          id: employee.id,
          type: 'Employees' as const,
        })) || []),
        { id: 'List', type: 'Employees' },
      ],
      query: (params) => ({
        method: 'GET',
        params: {
          ...params,
        },
        url: createUrl(ApiPath.Employees),
      }),
    }),
    updateEmployee: build.mutation<
      EmployeeResponsePrivate,
      UpdateEmployeeBodyPrivate & { id: number }
    >({
      invalidatesTags: (result) => [{ id: result?.id, type: 'Employees' }],
      onQueryStarted: (arg, api) => {
        api.queryFulfilled.then(() => {
          api.dispatch(employeesApi.util.invalidateTags(['Employees']));
        });
      },
      query: ({ id, ...data }) => ({
        data,
        method: 'PATCH',
        url: createUrl(ApiPath.Employees, id),
      }),
    }),
    updateEmployeeAuthPassword: build.mutation<
      EmployeeAuthPasswordResponsePrivate,
      UpdateEmployeeAuthPasswordBodyPrivate & { id: number }
    >({
      query: ({ id, ...data }) => ({
        data,
        method: 'POST',
        url: createUrl(ApiPath.Employees, id, ApiPath.AuthPassword),
      }),
    }),
  }),
  reducerPath: 'employeesApi',
  tagTypes: ['Employees'],
});

export const {
  useCreateEmployeeMutation,
  useGetEmployeeAuthPasswordByIdQuery,
  useGetEmployeesListQuery,
  useLazyGetEmployeeByIdQuery,
  useUpdateEmployeeAuthPasswordMutation,
  useUpdateEmployeeMutation,
} = employeesApi;
